// Oil price model (DESIGN §6.1).
//   spot = basePriceForDay(day) x eventMult x exp(noise)       noise: OU, 0.985*noise + N(0, sigma_era)
// basePriceForDay: OIL_PRICES annual values placed at mid-year, linearly interpolated; years covered by a
// PRICE_SHOCKS series use its monthly values (placed mid-month, scaled so the year's mean equals OIL_PRICES).
// 2020-04-20 returns the negative WTI print. Scripted priceOverride news pins spot near the override price.
// After 2025: procedural mean-reverting model around $75 x demandTrend(year) with Poisson shocks (rng-driven).
import { OIL_PRICES, PRICE_SHOCKS } from '../data/oilPrices.js';
import { SCRIPTED, HISTORICAL_NEWS, scriptedFlag } from '../data/news.js';
import { FIELDS } from '../data/fields.js';
import { EQUIPMENT } from '../data/equipment.js';
import { dateToDay, dayToDate, yearFrac } from '../core/time.js';
import { normal, chance, range, int, pick } from '../core/rng.js';
import { emit } from '../core/bus.js';
import { m3PriceMult } from '../sim/m3/prices.js'; // M3 econ: US quota/controls, posted vs realized, short-haul premium (data-only module)

export const LAST_DATA_YEAR = 2025;
export const HISTORY_EVERY_DAYS = 7;
const OU_PHI = 0.985;

// ------------------------------------------------------------------ deterministic base curve
const shockYears = new Map(); // year -> 12 scaled monthly values
for (const sh of PRICE_SHOCKS) {
  const [sy, sm] = sh.start.split('-').map(Number);
  sh.monthly.forEach((v, i) => {
    const y = sy + Math.floor((sm - 1 + i) / 12);
    const m = (sm - 1 + i) % 12;
    if (!shockYears.has(y)) shockYears.set(y, new Array(12).fill(null));
    shockYears.get(y)[m] = v;
  });
}
for (const [y, arr] of shockYears) {
  if (arr.some((v) => v == null) || OIL_PRICES[y] == null) { shockYears.delete(y); continue; }
  const mean = arr.reduce((a, b) => a + b, 0) / 12;
  shockYears.set(y, arr.map((v) => v * OIL_PRICES[y] / mean));
}

// Key points [fractionalYear, price], sorted.
const KEYS = [];
const years = Object.keys(OIL_PRICES).map(Number).sort((a, b) => a - b);
for (const y of years) {
  const m = shockYears.get(y);
  if (m) {
    for (let i = 0; i < 12; i++) {
      const mid = yearFrac(dateToDay(y, i + 1, 15));
      KEYS.push([mid, m[i]]);
    }
  } else KEYS.push([y + 0.5, OIL_PRICES[y]]);
}

const NEGATIVE = PRICE_SHOCKS.find((s) => s.negativePriceEvent)?.negativePriceEvent;
const NEG_DAY = NEGATIVE ? dateToDay(...NEGATIVE.date.split('-').map(Number)) : null;

// Post-2025 mean: blend from the last data point to $75 over ~3 years, then demand decline after 2035.
export function demandTrend(year) {
  return year <= 2035 ? 1 : Math.pow(0.994, year - 2035);
}
function proceduralMean(t) {
  const last = KEYS[KEYS.length - 1];
  const blend = Math.min(1, Math.max(0, (t - last[0]) / 3));
  return (last[1] + (75 - last[1]) * blend) * demandTrend(t);
}

export function basePriceForDay(day) {
  if (day === NEG_DAY) return NEGATIVE.price;
  const t = yearFrac(day);
  if (t <= KEYS[0][0]) return KEYS[0][1];
  const last = KEYS[KEYS.length - 1];
  if (t >= last[0]) return proceduralMean(t);
  // binary search
  let lo = 0, hi = KEYS.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (KEYS[mid][0] <= t) lo = mid; else hi = mid; }
  const [t0, v0] = KEYS[lo], [t1, v1] = KEYS[hi];
  return v0 + (v1 - v0) * (t - t0) / (t1 - t0);
}

// ------------------------------------------------------------------ scripted / historical effects
const OVERRIDES = SCRIPTED.filter((s) => s.effect?.type === 'priceOverride');
const HIST_FX = [...HISTORICAL_NEWS, ...SCRIPTED].filter((n) => n.priceEffect && n.priceEffect !== 1 && !shockYears.has(dayToDate(n.day).year));
const HIST_FX_DAYS = 30;

export function activeOverride(day) {
  let cur = null;
  for (const o of OVERRIDES) if (day >= o.day && day < o.day + o.effect.days) cur = o;
  return cur;
}

// Flavor multiplier from HISTORICAL_EVENTS.priceEffect: full at the event, fading linearly over 30 days.
function historicalMult(day) {
  let m = 1;
  for (const n of HIST_FX) {
    const age = day - n.day;
    if (age >= 0 && age < HIST_FX_DAYS) m *= 1 + (n.priceEffect - 1) * (1 - age / HIST_FX_DAYS);
  }
  return m;
}

export function sigmaForYear(year) {
  if (year <= 1900) return 0.03;
  if (year <= 1972) return 0.015;
  return 0.02;
}

// ------------------------------------------------------------------ procedural shocks (post-2025)
const SHOCK_RATE_PER_DAY = 1 / (8 * 365);
const SHOCK_UP = [
  'Strait Closure Fears Send Crude Soaring',
  'Cyberattack Knocks Out Gulf Export Terminals',
  'Hurricane Season From Hell: Offshore Output Shut In',
  'Producer Cartel Agrees Deep Output Cut',
  'Pipeline Sabotage Chokes Supply; Prices Spike',
];
const SHOCK_DOWN = [
  'Demand Slump as Electric Fleets Pass Tipping Point',
  'Price War Erupts as Cartel Talks Collapse',
  'Global Recession Fears Hammer Oil',
  'Mega-Field Comes Online; Glut Looms',
  'Storage Overflows as Mild Winter Crushes Demand',
];

// ------------------------------------------------------------------ public API
export function initMarket(state) {
  const spot = basePriceForDay(state.day);
  state.market = {
    spot, base: spot, noise: 0, activeEvents: [], history: [[state.day, round(spot)]], lastHistoryDay: state.day,
    contracts: [], regional: { pennsylvania: { discount: 0 } }, propellant: null, shock: null,
  };
  updateEvents(state);
  state.market.spot = computeSpot(state);
  return state.market;
}

export function tickMarket(state) {
  const m = state.market ?? initMarket(state);
  const { year } = dayToDate(state.day);
  m.noise = OU_PHI * m.noise + normal(state, 0, sigmaForYear(year));
  if (year > LAST_DATA_YEAR) proceduralShocks(state, m);
  updateEvents(state);
  m.base = basePriceForDay(state.day);
  m.spot = computeSpot(state);
  const ov = activeOverride(state.day);
  const special = (ov && ov.day === state.day) || state.day === NEG_DAY || (ov && state.day === ov.day + ov.effect.days - 1);
  if (state.day - m.lastHistoryDay >= HISTORY_EVERY_DAYS || special) {
    m.history.push([state.day, round(m.spot)]);
    m.lastHistoryDay = state.day;
  }
  return m.spot;
}

export function spotPrice(state) {
  return state.market ? state.market.spot : basePriceForDay(state.day);
}

function computeSpot(state) {
  const m = state.market;
  const day = state.day;
  if (day === NEG_DAY) return NEGATIVE.price;
  const ov = activeOverride(day);
  if (ov) return ov.effect.price * Math.exp(m.noise * 0.25);
  let mult = historicalMult(day);
  if (m.shock && day < m.shock.untilDay) mult *= m.shock.mult;
  return basePriceForDay(day) * mult * Math.exp(m.noise);
}

function updateEvents(state) {
  const m = state.market;
  const ov = activeOverride(state.day);
  const ev = [];
  if (ov) ev.push({ id: ov.id, untilDay: ov.day + ov.effect.days, type: 'priceOverride', price: ov.effect.price });
  if (m.shock && state.day < m.shock.untilDay) ev.push({ id: m.shock.id, untilDay: m.shock.untilDay, type: 'shock', mult: m.shock.mult });
  else if (m.shock) m.shock = null;
  m.activeEvents = ev;
  // Standard Oil local-buyer discount (DESIGN §6.1): 10% from 1872 unless on an independent pipeline.
  m.regional.pennsylvania.discount = flagOn(state, 'standardOilDiscount') ? 0.10 : 0;
}

function proceduralShocks(state, m) {
  if (m.shock && state.day < m.shock.untilDay) return;
  if (!chance(state, SHOCK_RATE_PER_DAY)) return;
  const up = chance(state, 0.5);
  const size = range(state, 0.25, 0.5);
  const days = int(state, 30, 365);
  const headline = pick(state, up ? SHOCK_UP : SHOCK_DOWN);
  m.shock = { id: `shock_${state.day}`, mult: up ? 1 + size : 1 - size, untilDay: state.day + days, headline };
  const item = { day: state.day, text: headline, kind: 'market' };
  if (Array.isArray(state.news)) state.news.push(item);
  emit('news', { ...item, pause: true });
}

function flagOn(state, flag) {
  const v = state.flags?.[flag];
  return v !== undefined ? !!v : !!scriptedFlag(flag, state.day);
}

// ------------------------------------------------------------------ local gluts (M2_SPEC §9)
// SCRIPTED effect { type: 'localPrice', fieldIds?, regionIds?, price?, mult?, days }: oil sold from those fields/regions
// fetches min(spot, price) and/or spot × mult while active. Same rule as data/m2/news.js localPriceAt, but over every
// SCRIPTED item (the M1 file carries the 1931 East Texas glut). Stateless, like activeOverride.
const LOCAL_FX = SCRIPTED.filter((s) => s.effect?.type === 'localPrice');
export function localPriceEffects(day, fieldId = null, regionId = null) {
  return LOCAL_FX.filter((n) => {
    const e = n.effect;
    if (day < n.day || day >= n.day + e.days) return false;
    return (fieldId && e.fieldIds?.includes(fieldId)) || (regionId && e.regionIds?.includes(regionId));
  });
}
export function localPrice(day, fieldId, regionId, spot) {
  let p = spot;
  for (const n of localPriceEffects(day, fieldId, regionId)) {
    if (n.effect.price != null) p = Math.min(p, n.effect.price);
    if (n.effect.mult != null) p *= n.effect.mult;
  }
  return p;
}
// Local price as a multiplier on spot (1 when no glut or spot <= 0).
export function localPriceMult(state, fieldId) {
  const spot = state.market?.spot ?? basePriceForDay(state.day);
  if (!(spot > 0)) return 1;
  const regionId = FIELDS[fieldId]?.regionId ?? null;
  if (!localPriceEffects(state.day, fieldId, regionId).length) return 1;
  return localPrice(state.day, fieldId, regionId, spot) / spot;
}

// Multiplier on spot for oil sold from a field: sour-crude quality and the Standard Oil local discount.
// transportId: the field's current transport def id (independent pipelines escape the discount).
export function netPriceMult(state, fieldId, transportId = null) {
  const f = FIELDS[fieldId];
  let mult = 1;
  if (f?.sour) mult *= flagOn(state, f.sour.flag) ? f.sour.multAfter : f.sour.mult;
  const disc = state.market?.regional?.[f?.regionId]?.discount ?? 0;
  const indep = transportId && EQUIPMENT[transportId]?.stats?.independent;
  if (disc && !indep) mult *= 1 - disc;
  mult *= localPriceMult(state, fieldId); // M2 local gluts (Spindletop 3¢, Cushing 40¢, East Texas 10¢)
  mult *= m3PriceMult(state, fieldId); // M3 econ (1 before 1946)
  return mult;
}

const round = (p) => Math.round(p * 100) / 100;
